import { logger } from '../logger.js';
// ── Shaders ──
// Vertex shader for the composite program — emits two UV varyings.
// v_uv: non-flipped, for sampling u_dst (FBO accumulator, GL y-up orientation).
// v_uv_flip: Y-flipped, for sampling u_src (Canvas2D-uploaded texture, needs flip
//   because texImage2D without UNPACK_FLIP_Y places source row 0 at texcoord y=0
//   which is the GL bottom, but we need screen-bottom to show image-bottom).
const VS_COMPOSITE = `#version 300 es
in vec2 a_pos;
out vec2 v_uv;
out vec2 v_uv_flip;
void main(){
  v_uv = a_pos * 0.5 + 0.5;
  v_uv_flip = vec2(v_uv.x, 1.0 - v_uv.y);
  gl_Position = vec4(a_pos, 0, 1);
}`;
// Vertex shader for FBO-to-FBO passes (adjustment layers).
// Both input (u_src = FBO) and output are in GL y-up orientation — no flip needed.
const VS_ADJ = `#version 300 es
in vec2 a_pos;
out vec2 v_uv;
void main(){
  v_uv = a_pos * 0.5 + 0.5;
  gl_Position = vec4(a_pos, 0, 1);
}`;
const FS_COMPOSITE = `#version 300 es
precision highp float;
in vec2 v_uv;
in vec2 v_uv_flip;
uniform sampler2D u_dst;
uniform sampler2D u_src;
uniform float u_opacity;
uniform int u_blend;
out vec4 fragColor;
vec3 blendNormal(vec3 b,vec3 s){return s;}
vec3 blendMultiply(vec3 b,vec3 s){return b*s;}
vec3 blendScreen(vec3 b,vec3 s){return 1.0-(1.0-b)*(1.0-s);}
vec3 blendOverlay(vec3 b,vec3 s){return mix(2.0*b*s,1.0-2.0*(1.0-b)*(1.0-s),step(0.5,b));}
vec3 blendDarken(vec3 b,vec3 s){return min(b,s);}
vec3 blendLighten(vec3 b,vec3 s){return max(b,s);}
vec3 blendDiff(vec3 b,vec3 s){return abs(b-s);}
void main(){
  vec4 dst=texture(u_dst,v_uv);
  vec4 src=texture(u_src,v_uv_flip);
  src.a*=u_opacity;
  if(src.a<0.004){fragColor=dst;return;}
  vec3 b=dst.rgb,s=src.rgb,blended;
  if(u_blend==0)blended=blendNormal(b,s);
  else if(u_blend==1)blended=blendMultiply(b,s);
  else if(u_blend==2)blended=blendScreen(b,s);
  else if(u_blend==3)blended=blendOverlay(b,s);
  else if(u_blend==4)blended=blendDarken(b,s);
  else if(u_blend==5)blended=blendLighten(b,s);
  else if(u_blend==6)blended=blendDiff(b,s);
  else blended=blendNormal(b,s);
  float outA=src.a+dst.a*(1.0-src.a);
  vec3 outRGB=(blended*src.a+dst.rgb*dst.a*(1.0-src.a))/max(outA,0.001);
  fragColor=vec4(outRGB,outA);
}`;
const FS_BRIGHTNESS = `#version 300 es
precision highp float;
in vec2 v_uv;
uniform sampler2D u_src;
uniform float u_brightness;
uniform float u_contrast;
out vec4 fragColor;
void main(){
  vec4 c=texture(u_src,v_uv);
  float br=u_brightness*0.5;
  float factor=(1.0+u_contrast)*(1.0+u_contrast);
  vec3 rgb=((c.rgb+br)-0.5)*factor+0.5;
  fragColor=vec4(clamp(rgb,0.0,1.0),c.a);
}`;
const FS_HUESAT = `#version 300 es
precision highp float;
in vec2 v_uv;
uniform sampler2D u_src;
uniform float u_hue;
uniform float u_sat;
uniform float u_light;
out vec4 fragColor;
vec3 rgb2hsl(vec3 c){
  float mx=max(c.r,max(c.g,c.b)),mn=min(c.r,min(c.g,c.b));
  float l=(mx+mn)*0.5,s=0.0,h=0.0;
  if(mx!=mn){float d=mx-mn;s=l>0.5?d/(2.0-mx-mn):d/(mx+mn);
  if(mx==c.r)h=(c.g-c.b)/d+(c.g<c.b?6.0:0.0);
  else if(mx==c.g)h=(c.b-c.r)/d+2.0;
  else h=(c.r-c.g)/d+4.0;h/=6.0;}return vec3(h,s,l);}
float hue2rgb(float p,float q,float t){
  if(t<0.0)t+=1.0;if(t>1.0)t-=1.0;
  if(t<1.0/6.0)return p+(q-p)*6.0*t;
  if(t<0.5)return q;if(t<2.0/3.0)return p+(q-p)*(2.0/3.0-t)*6.0;return p;}
vec3 hsl2rgb(vec3 hsl){
  if(hsl.y==0.0)return vec3(hsl.z);
  float q=hsl.z<0.5?hsl.z*(1.0+hsl.y):hsl.z+hsl.y-hsl.z*hsl.y;
  float p=2.0*hsl.z-q;
  return vec3(hue2rgb(p,q,hsl.x+1.0/3.0),hue2rgb(p,q,hsl.x),hue2rgb(p,q,hsl.x-1.0/3.0));}
void main(){
  vec4 c=texture(u_src,v_uv);if(c.a<0.004){fragColor=c;return;}
  vec3 hsl=rgb2hsl(c.rgb);
  hsl.x=fract(hsl.x+u_hue/360.0);
  hsl.y=clamp(hsl.y*(1.0+u_sat),0.0,1.0);
  hsl.z=clamp(hsl.z+u_light*0.5,0.0,1.0);
  fragColor=vec4(hsl2rgb(hsl),c.a);
}`;
const FS_INVERT = `#version 300 es
precision highp float;
in vec2 v_uv;
uniform sampler2D u_src;
out vec4 fragColor;
void main(){vec4 c=texture(u_src,v_uv);fragColor=vec4(1.0-c.r,1.0-c.g,1.0-c.b,c.a);}`;
const FS_LEVELS = `#version 300 es
precision highp float;
in vec2 v_uv;
uniform sampler2D u_src;
uniform float u_inBlack,u_inWhite,u_gamma,u_outBlack,u_outWhite;
out vec4 fragColor;
void main(){
  vec4 c=texture(u_src,v_uv);
  float range=u_inWhite-u_inBlack;if(range<0.001)range=0.001;
  vec3 v=clamp((c.rgb*255.0-u_inBlack)/range,0.0,1.0);
  v=pow(v,vec3(1.0/u_gamma));
  v=u_outBlack/255.0+v*(u_outWhite-u_outBlack)/255.0;
  fragColor=vec4(clamp(v,0.0,1.0),c.a);
}`;
// ── Blend name → shader int ──
const BLEND_ID = {
    'source-over': 0, 'multiply': 1, 'screen': 2, 'overlay': 3,
    'darken': 4, 'lighten': 5, 'difference': 6,
    'color-dodge': 0, 'color-burn': 0, 'hard-light': 3, 'soft-light': 3,
};
// ── Adjustment type → program key ──
const ADJ_PROGRAM = {
    brightness: 'brightness', huesat: 'huesat', invert: 'invert', levels: 'levels',
};
export class GLCompositor {
    ready = false;
    gl = null;
    programs = {};
    quad = null;
    fbos = [null, null];
    fboTexs = [null, null];
    texCache = new Map();
    lastReadIdx;
    ulocCache;
    /** Cached uniform locations for adjustment shader programs — avoids gl.getUniformLocation per frame */
    adjUlocCache = new Map();
    // ── WebGL context loss recovery ──
    canvas;
    contextLost = false;
    reinitAttempts = 0;
    static MAX_REINIT_ATTEMPTS = 2;
    /** Callback invoked when context is lost (true) or restored (false). */
    onContextLost = null;
    boundContextLost = null;
    boundContextRestored = null;
    constructor(canvas) {
        this.canvas = canvas;
        try {
            const gl = canvas.getContext('webgl2', {
                alpha: true, premultipliedAlpha: false, antialias: false,
            });
            if (!gl)
                return;
            this.gl = gl;
            this.initShaders();
            this.initQuad();
            this.installContextLossHandlers(canvas);
            this.ready = true;
        }
        catch {
            // WebGL2 unavailable — ready stays false
        }
    }
    // ── Context loss event handlers ──
    installContextLossHandlers(canvas) {
        // OffscreenCanvas may not support addEventListener in all browsers — guard.
        if (typeof canvas.addEventListener !== 'function')
            return;
        this.boundContextLost = (e) => {
            e.preventDefault(); // Allows restore to fire
            this.contextLost = true;
            this.ready = false;
            logger.warn('[GLCompositor] WebGL context lost');
            this.onContextLost?.(true);
        };
        this.boundContextRestored = () => {
            logger.log('[GLCompositor] WebGL context restored — reinitializing');
            this.contextLost = false;
            this.reinitAttempts++;
            if (this.reinitAttempts > GLCompositor.MAX_REINIT_ATTEMPTS) {
                logger.error('[GLCompositor] Exceeded reinit attempts — staying in fallback mode');
                this.onContextLost?.(false);
                return;
            }
            try {
                // After context restore the GL state machine is reset — all objects are invalid.
                // Re-acquire the context and rebuild shaders, quad, FBOs, and textures.
                const gl = this.canvas.getContext('webgl2', {
                    alpha: true, premultipliedAlpha: false, antialias: false,
                });
                if (!gl) {
                    logger.error('[GLCompositor] Failed to re-acquire WebGL2 context after restore');
                    return;
                }
                this.gl = gl;
                this.programs = {};
                this.quad = null;
                this.fbos = [null, null];
                this.fboTexs = [null, null];
                this.ulocCache = undefined;
                this.adjUlocCache.clear();
                this.lastReadIdx = undefined;
                // All cached textures are invalid — clear and force re-upload on next composite.
                this.texCache.clear();
                this.initShaders();
                this.initQuad();
                this.ready = true;
                logger.log('[GLCompositor] Reinitialized successfully (attempt ' + this.reinitAttempts + ')');
            }
            catch (err) {
                logger.error('[GLCompositor] Reinit failed:', err);
            }
            this.onContextLost?.(false);
        };
        canvas.addEventListener('webglcontextlost', this.boundContextLost);
        canvas.addEventListener('webglcontextrestored', this.boundContextRestored);
    }
    removeContextLossHandlers() {
        const canvas = this.canvas;
        if (typeof canvas?.removeEventListener !== 'function')
            return;
        if (this.boundContextLost)
            canvas.removeEventListener('webglcontextlost', this.boundContextLost);
        if (this.boundContextRestored)
            canvas.removeEventListener('webglcontextrestored', this.boundContextRestored);
        this.boundContextLost = null;
        this.boundContextRestored = null;
    }
    // ── Shader compilation ──
    compile(src, type) {
        const gl = this.gl;
        const sh = gl.createShader(type);
        gl.shaderSource(sh, src);
        gl.compileShader(sh);
        if (!gl.getShaderParameter(sh, gl.COMPILE_STATUS)) {
            logger.error('GLCompositor shader:', gl.getShaderInfoLog(sh));
            gl.deleteShader(sh);
            return null;
        }
        return sh;
    }
    link(vs, fs) {
        const gl = this.gl;
        const pg = gl.createProgram();
        gl.attachShader(pg, vs);
        gl.attachShader(pg, fs);
        gl.linkProgram(pg);
        if (!gl.getProgramParameter(pg, gl.LINK_STATUS)) {
            logger.error('GLCompositor link:', gl.getProgramInfoLog(pg));
            return null;
        }
        return pg;
    }
    initShaders() {
        const gl = this.gl;
        const vsComposite = this.compile(VS_COMPOSITE, gl.VERTEX_SHADER);
        const vsAdj = this.compile(VS_ADJ, gl.VERTEX_SHADER);
        // Composite: VS_COMPOSITE (dual UVs — flipped for src, non-flipped for dst)
        const fsComp = this.compile(FS_COMPOSITE, gl.FRAGMENT_SHADER);
        this.programs.composite = this.link(vsComposite, fsComp);
        // Adjustments: VS_ADJ (single non-flipped UV, FBO→FBO)
        const makeAdj = (fsSrc) => {
            const fs = this.compile(fsSrc, gl.FRAGMENT_SHADER);
            return this.link(vsAdj, fs);
        };
        this.programs.brightness = makeAdj(FS_BRIGHTNESS);
        this.programs.huesat = makeAdj(FS_HUESAT);
        this.programs.invert = makeAdj(FS_INVERT);
        this.programs.levels = makeAdj(FS_LEVELS);
    }
    initQuad() {
        const gl = this.gl;
        this.quad = gl.createBuffer();
        gl.bindBuffer(gl.ARRAY_BUFFER, this.quad);
        gl.bufferData(gl.ARRAY_BUFFER, new Float32Array([-1, -1, 1, -1, -1, 1, 1, 1]), gl.STATIC_DRAW);
    }
    // ── FBO management ──
    ensureFBOs(w, h) {
        const gl = this.gl;
        let anyRecreated = false;
        for (let i = 0; i < 2; i++) {
            const existing = this.fboTexs[i];
            if (existing && existing._w === w && existing._h === h)
                continue;
            if (this.fbos[i])
                gl.deleteFramebuffer(this.fbos[i]);
            if (existing)
                gl.deleteTexture(existing);
            const tex = gl.createTexture();
            gl.bindTexture(gl.TEXTURE_2D, tex);
            gl.texImage2D(gl.TEXTURE_2D, 0, gl.RGBA8, w, h, 0, gl.RGBA, gl.UNSIGNED_BYTE, null);
            gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, gl.LINEAR);
            gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAG_FILTER, gl.LINEAR);
            gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_S, gl.CLAMP_TO_EDGE);
            gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_T, gl.CLAMP_TO_EDGE);
            tex._w = w;
            tex._h = h;
            const fbo = gl.createFramebuffer();
            gl.bindFramebuffer(gl.FRAMEBUFFER, fbo);
            gl.framebufferTexture2D(gl.FRAMEBUFFER, gl.COLOR_ATTACHMENT0, gl.TEXTURE_2D, tex, 0);
            this.fbos[i] = fbo;
            this.fboTexs[i] = tex;
            anyRecreated = true;
        }
        gl.bindFramebuffer(gl.FRAMEBUFFER, null);
        // FBOs recreated → cached lastReadIdx points to now-empty textures.
        // Force a full re-composite on next frame so the fast-path blit isn't used.
        if (anyRecreated) {
            this.lastReadIdx = undefined;
        }
    }
    // ── Texture cache ──
    getOrUploadTexture(id, source, w, h, dirty) {
        const gl = this.gl;
        let entry = this.texCache.get(id);
        if (!entry) {
            const tex = gl.createTexture();
            gl.bindTexture(gl.TEXTURE_2D, tex);
            gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, gl.LINEAR);
            gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAG_FILTER, gl.LINEAR);
            gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_S, gl.CLAMP_TO_EDGE);
            gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_T, gl.CLAMP_TO_EDGE);
            gl.texImage2D(gl.TEXTURE_2D, 0, gl.RGBA8, w, h, 0, gl.RGBA, gl.UNSIGNED_BYTE, null);
            entry = { tex, w: 0, h: 0, uploaded: false };
            this.texCache.set(id, entry);
        }
        if (entry.w !== w || entry.h !== h) {
            gl.bindTexture(gl.TEXTURE_2D, entry.tex);
            gl.texImage2D(gl.TEXTURE_2D, 0, gl.RGBA8, w, h, 0, gl.RGBA, gl.UNSIGNED_BYTE, null);
            entry.w = w;
            entry.h = h;
            entry.uploaded = false;
        }
        // Only re-upload pixel data when dirty (or first upload)
        if (dirty !== false || !entry.uploaded) {
            gl.bindTexture(gl.TEXTURE_2D, entry.tex);
            gl.texSubImage2D(gl.TEXTURE_2D, 0, 0, 0, gl.RGBA, gl.UNSIGNED_BYTE, source);
            entry.uploaded = true;
        }
        return entry.tex;
    }
    drawQuad(program) {
        const gl = this.gl;
        gl.useProgram(program);
        const pos = gl.getAttribLocation(program, 'a_pos');
        gl.bindBuffer(gl.ARRAY_BUFFER, this.quad);
        gl.enableVertexAttribArray(pos);
        gl.vertexAttribPointer(pos, 2, gl.FLOAT, false, 0, 0);
        gl.drawArrays(gl.TRIANGLE_STRIP, 0, 4);
    }
    // ── Public API ──
    /**
     * Composite a list of layers using WebGL2.
     * Returns true if rendering succeeded, false if caller should fall back.
     */
    composite(layers, width, height) {
        if (!this.ready || !this.gl || this.contextLost)
            return false;
        const gl = this.gl;
        gl.viewport(0, 0, width, height);
        this.ensureFBOs(width, height);
        // Check if any layer needs re-compositing
        let anyDirty = false;
        for (const layer of layers) {
            if (layer.dirty !== false) {
                anyDirty = true;
                break;
            }
        }
        // Fast path: nothing changed — just re-blit the last composite
        if (!anyDirty && this.lastReadIdx !== undefined) {
            gl.bindFramebuffer(gl.FRAMEBUFFER, null);
            gl.viewport(0, 0, gl.canvas.width, gl.canvas.height);
            gl.bindFramebuffer(gl.READ_FRAMEBUFFER, this.fbos[this.lastReadIdx]);
            gl.bindFramebuffer(gl.DRAW_FRAMEBUFFER, null);
            gl.blitFramebuffer(0, 0, width, height, 0, 0, gl.canvas.width, gl.canvas.height, gl.COLOR_BUFFER_BIT, gl.NEAREST);
            gl.bindFramebuffer(gl.READ_FRAMEBUFFER, null);
            return true;
        }
        // Cache uniform locations for composite program
        if (!this.ulocCache) {
            const prog = this.programs.composite;
            this.ulocCache = {
                dst: gl.getUniformLocation(prog, 'u_dst'),
                src: gl.getUniformLocation(prog, 'u_src'),
                opacity: gl.getUniformLocation(prog, 'u_opacity'),
                blend: gl.getUniformLocation(prog, 'u_blend'),
            };
        }
        let readIdx = 0, writeIdx = 1;
        gl.bindFramebuffer(gl.FRAMEBUFFER, this.fbos[readIdx]);
        gl.clearColor(0, 0, 0, 0);
        gl.clear(gl.COLOR_BUFFER_BIT);
        const ulocs = this.ulocCache;
        for (const layer of layers) {
            // ── Adjustment layer → shader pass on accumulated composite ──
            if (layer.isAdjustment && layer.adjType) {
                const progKey = ADJ_PROGRAM[layer.adjType];
                const prog = progKey ? this.programs[progKey] : null;
                if (!prog)
                    continue; // no shader for this adj type
                gl.bindFramebuffer(gl.FRAMEBUFFER, this.fbos[writeIdx]);
                gl.useProgram(prog);
                gl.activeTexture(gl.TEXTURE0);
                gl.bindTexture(gl.TEXTURE_2D, this.fboTexs[readIdx]);
                const adjU = this.getAdjUlocs(prog, layer.adjType);
                gl.uniform1i(adjU.src, 0);
                this.setAdjUniforms(prog, layer.adjType, layer.adjParams || {});
                this.drawQuad(prog);
                [readIdx, writeIdx] = [writeIdx, readIdx];
                continue;
            }
            // ── Raster layer → upload texture (dirty-aware) + blend ──
            const w = layer.width || layer.source.width || width;
            const h = layer.height || layer.source.height || height;
            const srcTex = this.getOrUploadTexture(layer.id, layer.source, w, h, layer.dirty);
            gl.bindFramebuffer(gl.FRAMEBUFFER, this.fbos[writeIdx]);
            const prog = this.programs.composite;
            gl.useProgram(prog);
            gl.activeTexture(gl.TEXTURE0);
            gl.bindTexture(gl.TEXTURE_2D, this.fboTexs[readIdx]);
            gl.uniform1i(ulocs.dst, 0);
            gl.activeTexture(gl.TEXTURE1);
            gl.bindTexture(gl.TEXTURE_2D, srcTex);
            gl.uniform1i(ulocs.src, 1);
            gl.uniform1f(ulocs.opacity, layer.opacity);
            gl.uniform1i(ulocs.blend, BLEND_ID[layer.blend] || 0);
            this.drawQuad(prog);
            [readIdx, writeIdx] = [writeIdx, readIdx];
        }
        // Blit result to screen (default framebuffer)
        this.lastReadIdx = readIdx;
        gl.bindFramebuffer(gl.READ_FRAMEBUFFER, this.fbos[readIdx]);
        gl.bindFramebuffer(gl.DRAW_FRAMEBUFFER, null);
        gl.viewport(0, 0, gl.canvas.width, gl.canvas.height);
        gl.blitFramebuffer(0, 0, width, height, 0, 0, gl.canvas.width, gl.canvas.height, gl.COLOR_BUFFER_BIT, gl.NEAREST);
        gl.bindFramebuffer(gl.READ_FRAMEBUFFER, null);
        return true;
    }
    /** Get or build the uniform location cache for an adjustment program. */
    getAdjUlocs(prog, adjType) {
        let cached = this.adjUlocCache.get(adjType);
        if (cached)
            return cached;
        const gl = this.gl;
        cached = {};
        if (adjType === 'brightness') {
            cached.brightness = gl.getUniformLocation(prog, 'u_brightness');
            cached.contrast = gl.getUniformLocation(prog, 'u_contrast');
        }
        else if (adjType === 'huesat') {
            cached.hue = gl.getUniformLocation(prog, 'u_hue');
            cached.sat = gl.getUniformLocation(prog, 'u_sat');
            cached.light = gl.getUniformLocation(prog, 'u_light');
        }
        else if (adjType === 'levels') {
            cached.inBlack = gl.getUniformLocation(prog, 'u_inBlack');
            cached.inWhite = gl.getUniformLocation(prog, 'u_inWhite');
            cached.gamma = gl.getUniformLocation(prog, 'u_gamma');
            cached.outBlack = gl.getUniformLocation(prog, 'u_outBlack');
            cached.outWhite = gl.getUniformLocation(prog, 'u_outWhite');
        }
        // u_src is always present on adjustment programs
        cached.src = gl.getUniformLocation(prog, 'u_src');
        this.adjUlocCache.set(adjType, cached);
        return cached;
    }
    setAdjUniforms(prog, adjType, params) {
        const gl = this.gl;
        const u = this.getAdjUlocs(prog, adjType);
        if (adjType === 'brightness') {
            gl.uniform1f(u.brightness, (params.brightness || 0) / 100);
            gl.uniform1f(u.contrast, (params.contrast || 0) / 100);
        }
        else if (adjType === 'huesat') {
            gl.uniform1f(u.hue, params.hue || 0);
            gl.uniform1f(u.sat, (params.saturation || 0) / 100);
            gl.uniform1f(u.light, (params.lightness || 0) / 100);
        }
        else if (adjType === 'levels') {
            gl.uniform1f(u.inBlack, params.inBlack || 0);
            gl.uniform1f(u.inWhite, params.inWhite || 255);
            gl.uniform1f(u.gamma, params.gamma || 1);
            gl.uniform1f(u.outBlack, params.outBlack || 0);
            gl.uniform1f(u.outWhite, params.outWhite || 255);
        }
    }
    /** Release all GPU resources */
    destroy() {
        this.removeContextLossHandlers();
        this.onContextLost = null;
        const gl = this.gl;
        if (!gl)
            return;
        this.texCache.forEach(e => gl.deleteTexture(e.tex));
        this.texCache.clear();
        for (let i = 0; i < 2; i++) {
            if (this.fbos[i])
                gl.deleteFramebuffer(this.fbos[i]);
            if (this.fboTexs[i])
                gl.deleteTexture(this.fboTexs[i]);
        }
        this.fbos = [null, null];
        this.fboTexs = [null, null];
        Object.values(this.programs).forEach(p => gl.deleteProgram(p));
        this.programs = {};
        if (this.quad)
            gl.deleteBuffer(this.quad);
        this.quad = null;
        this.ulocCache = undefined;
        this.adjUlocCache.clear();
        this.lastReadIdx = undefined;
        this.gl = null;
        this.ready = false;
    }
}
//# sourceMappingURL=gl-compositor.js.map